import React, { useEffect, useState } from 'react';
import axios, { type AxiosError } from 'axios'; // Import axios and AxiosError

import CardDialog from '~/components/CardDialog'; // Import CardDialog
import AlertBanner from '~/components/core/AlertBanner';
import AutoSaveSwitch from '~/components/core/Atomic/AutoSaveSwitch';
import Skeleton from '~/components/core/Atomic/Skeleton';
import Tooltip from '~/components/core/Atomic/Tooltip';
import LoadingSwitch from '~/components/core/Loading/LoadingSwitch';
import Text from '~/components/core/TextComponents/Text';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';

import { reportAxiosError } from '../../../Utils'; // Import reportAxiosError

import type { EmailGeneralConfigurationData } from './emailConfigurationTypes';
import texts from './texts';

interface EmailGeneralConfigurationProps {
  emailGeneralConfiguration?: EmailGeneralConfigurationData;
  isLoading: boolean;
  isError: boolean;
  reloadData: () => void;
}

const TEXTS = texts.generalEmailSettings;

const EmailGeneralConfiguration: React.FC<EmailGeneralConfigurationProps> = ({
  emailGeneralConfiguration,
  isLoading,
  isError,
  reloadData,
}) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const [shouldExtract, setShouldExtract] = useState(false);
  const [supervisorSendAsOtherAdjuster, setSupervisorSendAsOtherAdjuster] = useState(false);
  const [restrictSendAsToManagedUsers, setRestrictSendAsToManagedUsers] = useState(true);
  const [personalEmailSignatureEnabled, setPersonalEmailSignatureEnabled] = useState(false);
  const [htmlAttachmentsEnabled, setHtmlAttachmentsEnabled] = useState(false);

  useEffect(() => {
    if (emailGeneralConfiguration) {
      setShouldExtract(emailGeneralConfiguration.should_extract_original_from);
      setSupervisorSendAsOtherAdjuster(emailGeneralConfiguration.supervisor_send_as_other_adjuster);
      setRestrictSendAsToManagedUsers(emailGeneralConfiguration.restrict_send_as_to_managed_users);
      setPersonalEmailSignatureEnabled(emailGeneralConfiguration.personal_email_signature_enabled);
      setHtmlAttachmentsEnabled(emailGeneralConfiguration.html_attachments_enabled);
    }
  }, [emailGeneralConfiguration]);

  // Helper function to update general configuration
  const updateGeneralConfiguration = async (updates: Partial<EmailGeneralConfigurationData>) => {
    try {
      await axios.put(`/api/v1/email_configuration/organizations/${organization.id}/general_configuration`, {
        should_extract_original_from: shouldExtract,
        supervisor_send_as_other_adjuster: supervisorSendAsOtherAdjuster,
        restrict_send_as_to_managed_users: restrictSendAsToManagedUsers,
        personal_email_signature_enabled: personalEmailSignatureEnabled,
        html_attachments_enabled: htmlAttachmentsEnabled,
        ...updates,
      });
      await reloadData();
    } catch (error: unknown) {
      await reportAxiosError(error as AxiosError);
    }
  };

  const handleExtractToggle = async (newCheckedState: boolean) => {
    await updateGeneralConfiguration({ should_extract_original_from: newCheckedState });
  };

  const handleSupervisorSendAsToggle = async (newCheckedState: boolean) => {
    const updates: Partial<EmailGeneralConfigurationData> = { supervisor_send_as_other_adjuster: newCheckedState };
    if (!newCheckedState) {
      updates.restrict_send_as_to_managed_users = true;
    }
    await updateGeneralConfiguration(updates);
  };

  const handleRestrictSendAsToggle = async (newCheckedState: boolean) => {
    await updateGeneralConfiguration({ restrict_send_as_to_managed_users: newCheckedState });
  };

  const handlePersonalEmailSignatureToggle = async (newCheckedState: boolean) => {
    await updateGeneralConfiguration({ personal_email_signature_enabled: newCheckedState });
  };

  const handleHtmlAttachmentsToggle = async (newCheckedState: boolean) => {
    await updateGeneralConfiguration({ html_attachments_enabled: newCheckedState });
  };

  const GeneralConfigurationSkeleton = () => (
    <div className="p-6">
      <Skeleton variant="text" width="40%" height={30} className="mb-4" />
      <div className="mb-6 flex items-center">
        <Skeleton variant="rectangular" width={50} height={30} className="mr-2" />
        <Skeleton variant="text" width="70%" height={30} />
      </div>
      <div className="mb-6 flex items-center">
        <Skeleton variant="rectangular" width={50} height={30} className="mr-2" />
        <Skeleton variant="text" width="70%" height={30} />
      </div>
      <Skeleton variant="rectangular" width={100} height={40} />
    </div>
  );

  return (
    <div className="m-20">
      <LoadingSwitch
        isLoading={isLoading}
        isError={isError && !emailGeneralConfiguration}
        LoadingComponent={GeneralConfigurationSkeleton}
        ErrorComponent={() => <AlertBanner alertType={AlertBanner.ALERT_TYPES.ERROR} note={TEXTS.errorLoading} />}
      >
        <CardDialog title={TEXTS.title}>
          <div className="p-6 mb-16 flex items-center md:p-24">
            <AutoSaveSwitch checked={shouldExtract} onSave={handleExtractToggle} disabled={isLoading} />
            <Tooltip title={TEXTS.extractOriginalFrom.tooltip}>
              <span>
                <Text variant={Text.VARIANTS.SM}>{TEXTS.extractOriginalFrom.label}</Text>
              </span>
            </Tooltip>
          </div>
          <div className="p-6 mb-16 flex items-center gap-24 md:p-24">
            <div className="flex items-center">
              <AutoSaveSwitch
                checked={supervisorSendAsOtherAdjuster}
                onSave={handleSupervisorSendAsToggle}
                disabled={isLoading}
              />
              <Tooltip title={TEXTS.supervisorSendAs.tooltip}>
                <span>
                  <Text variant={Text.VARIANTS.SM}>{TEXTS.supervisorSendAs.label}</Text>
                </span>
              </Tooltip>
            </div>
            {supervisorSendAsOtherAdjuster ? (
              <div className="flex items-center">
                <AutoSaveSwitch
                  checked={restrictSendAsToManagedUsers}
                  onSave={handleRestrictSendAsToggle}
                  disabled={isLoading}
                />
                <Tooltip title={TEXTS.restrictSendAs.tooltip}>
                  <span>
                    <Text variant={Text.VARIANTS.SM}>{TEXTS.restrictSendAs.label}</Text>
                  </span>
                </Tooltip>
              </div>
            ) : null}
          </div>
          <div className="p-6 mb-16 flex items-center md:p-24">
            <AutoSaveSwitch
              checked={personalEmailSignatureEnabled}
              onSave={handlePersonalEmailSignatureToggle}
              disabled={isLoading}
            />
            <Tooltip title={TEXTS.personalEmailSignature.tooltip}>
              <span>
                <Text variant={Text.VARIANTS.SM}>{TEXTS.personalEmailSignature.label}</Text>
              </span>
            </Tooltip>
          </div>
          <div className="p-6 mb-16 flex items-center md:p-24">
            <AutoSaveSwitch
              checked={htmlAttachmentsEnabled}
              onSave={handleHtmlAttachmentsToggle}
              disabled={isLoading}
            />
            <Tooltip title={TEXTS.htmlAttachments.tooltip}>
              <span>
                <Text variant={Text.VARIANTS.SM}>{TEXTS.htmlAttachments.label}</Text>
              </span>
            </Tooltip>
          </div>
        </CardDialog>
      </LoadingSwitch>
    </div>
  );
};

export default EmailGeneralConfiguration;
